/**
 * Hoe oud de vastgelegde regelingdata is.
 *
 * Aanleiding: de seed van de zes regelingen dateert van 8 april 2026
 * (migratie `20260408120000_subsidie_module_tabellen_en_seed.sql`) en stond op
 * 8 september 2026 nog steeds ongewijzigd in de database — 153 dagen — zonder
 * dat iemand dat aan het scherm kon zien. Migratie 060 voegde daarom
 * `sub_regelingen.bron_gecontroleerd_op` (date, nullable) toe.
 *
 * Deze module is puur: geen React, geen Supabase, geen `new Date()` binnenin.
 * "Vandaag" komt van de aanroeper, zodat de test er een vaste dag in kan zetten.
 */

/**
 * Vanaf hoeveel dagen een regeling als verouderd geldt.
 *
 * 90 dagen is een afspraak, geen RVO-voorschrift. De keuze volgt de aanbeveling
 * uit het SPRILA-onderzoek van 8-9-2026: de deelbudgetten verschuiven sneller
 * dan de tarieven, en een controle "per maand tot 18 december" is daar zinvol.
 * Een kwartaal is de ruimste termijn die daar nog bij past.
 */
export const CONTROLE_VERVALTERMIJN_DAGEN = 90;

export type BronVersheidStatus = 'nooit' | 'vers' | 'verouderd';

export interface BronVersheid {
  status: BronVersheidStatus;
  /** Aantal dagen sinds de controle; `null` als er geen datum staat. */
  dagen: number | null;
  /** Korte zin voor het scherm. */
  tekst: string;
}

/**
 * Dagen tussen de controledatum en vandaag.
 *
 * Rekent in hele UTC-dagen: `bron_gecontroleerd_op` is een `date` zonder tijd
 * of zone, dus een berekening over lokale milliseconden zou rond de zomertijd
 * een dag kunnen schelen. Een datum in de toekomst levert een negatief getal;
 * dat is geen fout maar informatie voor de aanroeper.
 */
export function dagenSindsControle(datum: string | null | undefined, vandaag: Date): number | null {
  if (!datum) return null;
  const delen = /^(\d{4})-(\d{2})-(\d{2})/.exec(datum);
  if (!delen) return null;
  const toen = Date.UTC(Number(delen[1]), Number(delen[2]) - 1, Number(delen[3]));
  const nu = Date.UTC(vandaag.getFullYear(), vandaag.getMonth(), vandaag.getDate());
  return Math.round((nu - toen) / 86_400_000);
}

function dagTekst(dagen: number): string {
  if (dagen <= 0) return 'vandaag gecontroleerd';
  if (dagen === 1) return '1 dag geleden gecontroleerd';
  return `${dagen} dagen geleden gecontroleerd`;
}

export function bronVersheid(datum: string | null | undefined, vandaag: Date): BronVersheid {
  const dagen = dagenSindsControle(datum, vandaag);
  if (dagen === null) {
    return {
      status: 'nooit',
      dagen: null,
      tekst: 'Bron nooit gecontroleerd — de bedragen en voorwaarden zijn ongeverifieerd.',
    };
  }
  if (dagen >= CONTROLE_VERVALTERMIJN_DAGEN) {
    return {
      status: 'verouderd',
      dagen,
      tekst: `${dagTekst(dagen)} — langer dan ${CONTROLE_VERVALTERMIJN_DAGEN} dagen. Controleer de RVO-pagina opnieuw.`,
    };
  }
  return { status: 'vers', dagen, tekst: dagTekst(dagen) };
}

/** Vandaag als `YYYY-MM-DD`, in de lokale kalender van de gebruiker. */
export function vandaagAlsDatum(vandaag: Date = new Date()): string {
  const maand = String(vandaag.getMonth() + 1).padStart(2, '0');
  const dag = String(vandaag.getDate()).padStart(2, '0');
  return `${vandaag.getFullYear()}-${maand}-${dag}`;
}
